import * as Yup from 'yup';
import AllForm from '../Stateless/Form';
import PrivilegeMatrix from '../Stateless/PrivilegeMatrix';
import { atom, useAtom } from 'jotai';
import { UseCreateSubAdmin, useEditSubAdmin } from '../Query/AddSubAdminQuery';
import { CircularProgress, Button, Box } from '@mui/material';
import { useState, useMemo } from 'react';
import { useThemeColors } from '../../hooks/useThemeColors';
import { FieldInputProps, FormikProps } from 'formik';
import PhoneNumberInput from './Utils/PhoneNumberInput';
import { modules, moduleActions } from '../Stateless/permissionConfig';
import { COLORS } from '../../theme/themeConstants';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter name')
    .max(50, 'Name cannot exceed 50 characters')
    .test(
      'no-leading-trailing-spaces',
      'Name cannot start or end with spaces',
      (value) => value?.trim() === value
    )
    .test(
      'not-only-spaces',
      'Name cannot be only spaces',
      (value) => value?.trim().length > 0
    )
    .test(
      'not-only-numbers',
      'Name cannot be only numbers',
      (value) => {
        if (!value) return false;
        return !/^\d+$/.test(value.trim());
      }
    )
    .test(
      'not-start-with-number',
      'Name cannot start with a number',
      (value) => {
        if (!value) return false;
        return !/^\d/.test(value.trim());
      }
    ),
   email: Yup.string()
     .matches(
       /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
       'Please enter a valid email address'
     )
     .required('Email is required'),
  mobile: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  password: Yup.string()
    .required('Please enter the password')
    .min(8, 'Password must be at least 8 characters')
    .max(15, 'Password must not exceed 15 characters')
    .matches(/^\S+$/, 'Password must not contain spaces')
    .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Password must contain at least one number')
    .matches(/(?=.*[@$!%*?&#^()_+=\-{}\[\]:;"'<>,.?/\\|`~])/, 'Password must contain at least one special character'),
  confirmPassword: Yup.string()
    .required('Please enter the confirm password')
    .oneOf([Yup.ref('password'), ''], 'Passwords must match')
    .min(8, 'Password must be at least 8 characters')
    .max(15, 'Password must not exceed 15 characters')
    .matches(/^\S+$/, 'Password must not contain spaces')
    .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Password must contain at least one number')
    .matches(/(?=.*[@$!%*?&#^()_+=\-{}\[\]:;"'<>,.?/\\|`~])/, 'Password must contain at least one special character'),
  permission: Yup.object()
    .test(
      'at-least-one-permission',
      'Please select at least one permission',
      (value) => {
        if (!value || typeof value !== 'object' || !('permissions' in value)) return false;
        const permissions = (value as any).permissions;
        if (!permissions || typeof permissions !== 'object') return false;
        return Object.values(permissions).some((module: any) =>
          (Object.values(module) as boolean[]).some((perm) => perm)
        );
      }
    )
});

export interface SubAdminData {
  id: string;
  name: string;
  email: string;
  mobileNumber: string;
  lastLoginDate: string;
  lastLogoutDate: string;
  lastLoginIp: string;
  status: string;
  privilege: boolean;
  permissions: any;
  serialNumber: number;
}
export const subAdminAtom = atom<SubAdminData | null>(null);

// Helper to generate permissions for each module using moduleActions structure
const generatePermissions = () => {
  const perms: any = {};
  modules.forEach((mod: string) => {
    perms[mod] = {};
    (moduleActions[mod] || []).forEach((act: string) => {
      perms[mod][act] = false;
    });
  });
  return perms;
};

const initialPrivileges = {
  privilege: false,
  permissions: generatePermissions(),
};

// Normalize incoming permissions from API to match UI action casing
// Supports API's preferred key "unBlock" for Unblock action
const normalizePermissions = (permissions: any) => {
  if (!permissions || typeof permissions !== 'object') return initialPrivileges.permissions;
  const result: any = {};
  modules.forEach((mod: string) => {
    result[mod] = {};
    (moduleActions[mod] || []).forEach((act: string) => {
      const lower = act.toLowerCase();
      const camel = act.charAt(0).toLowerCase() + act.slice(1);
      const apiPreferred = act === 'Unblock' ? 'unBlock' : undefined;
      const modPerms = permissions[mod] || {};
      result[mod][act] =
        modPerms[lower] ??
        (apiPreferred ? modPerms[apiPreferred] : undefined) ??
        modPerms[act] ??
        modPerms[camel] ??
        false;
    });
  });
  return result;
};

const AddSubAdmin = ({ onCancel }: { onCancel: () => void }) => {
  const { warning } = useThemeColors();
  // const setSubAdminAtom = useSetAtom(subAdminAtom);
  const [subAdminData] = useAtom(subAdminAtom);

  const { mutate: CreateSubAdmin, isPending: CreateSubAdminLoading } =
    UseCreateSubAdmin();

  // const fullNumber = subAdminData?.mobileNumber;
  // const countryCode = fullNumber?.slice(0, -10);
  const [selectedCountryCode, setSelectedCountryCode] = useState("+962");

  const { mutate: EditSubAdmin } = useEditSubAdmin();

  // Get initial privileges from subAdminData if it exists, otherwise use default
  const initialPrivilegeValues = useMemo(() => subAdminData
    ? {
      privilege: subAdminData.privilege || false,
      permissions: normalizePermissions(subAdminData.permissions),
    }
    : initialPrivileges, [subAdminData]);

  // Remove the problematic useEffect that was causing infinite loops
  // The subAdminAtom will be managed by the parent component
  return (
    <>
      {AllForm({
        initialValues: {
          name: subAdminData?.name || '',
          email: subAdminData?.email || '',
          mobile: subAdminData?.mobileNumber.slice(selectedCountryCode?.length) || '',
          password: '',
          confirmPassword: '',
          permission: initialPrivilegeValues,
        },
        validationSchema,
        onSubmit: async (values: any) => {
          // Transform permissions to API keys (lowercase, with special-case for Unblock => unBlock)
          const transformPermissions = (permissions: any) => {
            const result: any = {};
            Object.keys(permissions).forEach((mod) => {
              result[mod] = {};
              Object.keys(permissions[mod]).forEach((act) => {
                const apiKey = act === 'Unblock' || act === 'unblock' ? 'unBlock' : act.toLowerCase();
                result[mod][apiKey] = permissions[mod][act];
              });
            });
            return result;
          };

          const modifiedValues = {
            name: values.name,
            email: values.email,
            mobileNumber: `${selectedCountryCode}${values.mobile}`,
            password: values.password,
            confirmPassword: values.confirmPassword,
            privilege: values.permission.privilege,
            permissions: transformPermissions(values.permission.permissions),
          };
          try {
            if (subAdminData) {
              EditSubAdmin(
                {
                  id: subAdminData.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                    // navigate('/sub-admin/list');
                    // closeForm();
                    // resetForm();
                  },
                  onError: (error: any) => {
                    onCancel();
                    console.log(error);
                    // setErrors(parseJavaError(error));
                  },
                }
              );
            } else {
              CreateSubAdmin(modifiedValues, {
                onSuccess: () => {
                  onCancel();
                  // navigate('/sub-admin/list');
                  // closeForm();
                  // resetForm();
                },
                onError: (error: any) => {
                  console.log(error);
                  // setErrors(parseJavaError(error));
                },
              });
            }
          } catch (error) {
            console.log(error);
            // setErrors(parseJavaError(error));
          }
        },

        fields: [
          {
            name: 'name',
            label: 'Name:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'email',
            label: 'Email:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'mobile',
            label: 'Mobile No:',
            type: 'custom',
            isRequired: true,
            component: ({
              field,
              form,
            }: {
              field: FieldInputProps<any>;
              form: FormikProps<any>;
            }) => (
              <PhoneNumberInput
                field={field}
                form={form}
                selectedCountryCode={selectedCountryCode}
                onCountryCodeChange={setSelectedCountryCode}
              />
            )
          },
          {
            name: 'password',
            label: 'Password:',
            type: 'password',
            isRequired: true,
          },
          {
            name: 'confirmPassword',
            label: 'Confirm Password:',
            type: 'password',
            isRequired: true,
          },
          {
            name: 'permission',
            type: 'customSelect',
            component: (props: any) => (
              <PrivilegeMatrix
                {...props}
                error={props.form.errors.permission}
                isTouched={props.form.touched.permission}
              />
            ),
            value: initialPrivilegeValues,
          },
        ],

        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* <Button
          type="submit"
          variant="contained"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            px: 3,
            py: 1,
            backgroundColor: 'rgb(76, 10, 39)',
            '&:hover': {
              backgroundColor: 'rgb(76, 10, 39, 0.8)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              color: 'rgba(0, 0, 0, 0.26)',
            },
          }}
          onClick={() => formikProps.handleSubmit()}
        >
          {subAdminData ? 'Update' : 'Create'}
        </Button> */}
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': {
                  backgroundColor: 'rgb(76, 10, 39, 0.8)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                // Mark all fields as touched to show validation errors
                Object.keys(formikProps.values).forEach((fieldName) => {
                  formikProps.setFieldTouched(fieldName, true, false);
                });
                // Check if there are validation errors
                if (Object.keys(formikProps.errors).length === 0) {
                  formikProps.handleSubmit();
                } else {
                  console.log('Validation failed:', formikProps.errors);
                }
              }}
              disabled={CreateSubAdminLoading}
            >
              {CreateSubAdminLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {subAdminData ? 'Updating...' : 'Creating...'}
                </>
              ) : subAdminData ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>

            <Button
              type="button"
              variant="outlined"
              onClick={() => formikProps.resetForm()}
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                borderColor: '#666',
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                  color: '#333',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              Reset
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff', // ← Make the text white
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff', // ← Ensure it stays white on hover too
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default AddSubAdmin;
